import { supabase } from '@/services/supabase/client';
import type { Tables } from '@/services/supabase/types';
import { pageRange } from '@/utils/helpers/pagination';

import type { Notification, NotificationData } from './types';

/**
 * Notificações pessoais — a fonte do segmento "Para você" da aba Avisos e do
 * badge de não lidas da barra inferior.
 *
 * ## Por que mora em `services/`, e não no módulo `alerts`
 *
 * O badge fica na `BottomNav`, que é `components/layouts/` e **não pode importar
 * de `modules/`** (docs/01). Notificação deixou de ser assunto de uma tela e
 * virou infraestrutura de conta, como a sessão e o CEP — então desceu para
 * `services/`, ao lado de `postal-code` e `geocoding`. O módulo `alerts`
 * continua dono da apresentação (ícone por evento, destino do toque); daqui sai
 * só o I/O.
 *
 * A RLS já restringe leitura e escrita ao destinatário (`notifications_select_own`,
 * `notifications_update_own`), então nenhuma consulta filtra por usuário: o
 * servidor é quem garante que só as suas linhas voltam — e que ninguém marca
 * como lida a notificação de outra pessoa.
 */

type NotificationRow = Tables<'notifications'>;

/**
 * `data` é `jsonb` livre: pode voltar como número, texto, array ou `null`. Só o
 * objeto vira carga — o resto não teria chave para ler e viraria `undefined` na
 * primeira leitura. `Array.isArray` é necessário porque array **é** objeto para
 * o `typeof`.
 */
function toNotificationData(value: NotificationRow['data']): NotificationData | null {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) return null;

  return value;
}

export function toNotification(row: NotificationRow): Notification {
  return {
    id: row.id,
    kind: row.kind,
    title: row.title,
    body: row.body,
    isRead: row.is_read,
    createdAt: new Date(row.created_at),
    data: toNotificationData(row.data),
  };
}

export const notificationsService = {
  /**
   * Uma página de avisos, do mais novo ao mais velho. Notificação lida não é
   * apagada — a caixa de quem usa o app há meses tem centenas de linhas, e
   * baixar todas para mostrar as de hoje é exatamente o que
   * `utils/helpers/pagination` existe para evitar.
   *
   * ⚠️ A contagem de não lidas **não** sai daqui: quem conta é `countUnread`,
   * com `head: true`. Contar sobre a lista passaria a contar só as páginas
   * carregadas — o badge da barra e o botão "Marcar todas como lidas" mentiriam
   * juntos.
   */
  async listMine(pageIndex: number): Promise<Notification[]> {
    const { data, error } = await supabase
      .from('notifications')
      .select('*')
      .order('created_at', { ascending: false })
      .range(...pageRange(pageIndex));

    if (error) throw error;

    return (data ?? []).map(toNotification);
  },

  /**
   * Só o número, sem trazer linha alguma (`head: true`): é a consulta que roda
   * de tempos em tempos em qualquer tela, e num app usado com a rede que sobrou
   * baixar a lista inteira para contar seria o pior jeito de gastar o que
   * existe de conexão.
   */
  async countUnread(): Promise<number> {
    const { count, error } = await supabase
      .from('notifications')
      .select('id', { count: 'exact', head: true })
      .eq('is_read', false);

    if (error) throw error;

    return count ?? 0;
  },

  /**
   * O `eq('is_read', false)` não é redundante com o `eq('id', …)`: ele evita a
   * escrita quando a notificação já estava lida — caso comum, porque marcar é
   * efeito colateral de tocar o cartão e a pessoa toca de novo no mesmo item.
   */
  async markRead(id: string): Promise<void> {
    const { error } = await supabase
      .from('notifications')
      .update({ is_read: true })
      .eq('id', id)
      .eq('is_read', false);

    if (error) throw error;
  },

  async markAllRead(): Promise<void> {
    const { error } = await supabase
      .from('notifications')
      .update({ is_read: true })
      .eq('is_read', false);

    if (error) throw error;
  },
};
